// src/components/common/ProductCard.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Star, Scale, DollarSign, Heart, Eye } from 'lucide-react';

function ProductCard({ product, isMobile, addToCart }) {
  const [purchaseType, setPurchaseType] = useState('weight');
  const [quantity, setQuantity] = useState(1);
  const [customAmount, setCustomAmount] = useState('');
  const [customWeight, setCustomWeight] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [isLiked, setIsLiked] = useState(false);

  const handleAddToCart = () => {
    if (purchaseType === 'amount') {
      if (!customAmount || parseFloat(customAmount) <= 0) {
        alert('Please enter a valid amount');
        return;
      }
      addToCart(product, 1, 'amount', parseFloat(customAmount));
    } else if (purchaseType === 'customWeight') {
      if (!customWeight || parseFloat(customWeight) <= 0) {
        alert('Please enter a valid weight');
        return;
      }
      addToCart(product, parseFloat(customWeight), 'weight');
    } else {
      addToCart(product, quantity, 'weight');
    }
    
    // Success animation
    const btn = document.querySelector(`#add-btn-${product.id}`);
    if (btn) {
      btn.classList.add('animate-success');
      setTimeout(() => btn.classList.remove('animate-success'), 300);
    }
    
    setQuantity(1);
    setCustomAmount('');
    setCustomWeight('');
  };

  const calculatedWeight = customAmount ? (customAmount / product.price).toFixed(2) : null;
  const calculatedAmount = customWeight ? (customWeight * product.price).toFixed(2) : null;

  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="group relative bg-gradient-to-b from-white to-gray-50 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100"
    >
      {/* Product badge */}
      {product.isWholesale && (
        <div className="absolute top-4 left-4 z-10">
          <span className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold rounded-full shadow-lg">
            📦 WHOLESALE
          </span>
        </div>
      )}
      
      {/* Like button */}
      <button
        onClick={() => setIsLiked(!isLiked)}
        className="absolute top-4 right-4 z-10 p-2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-red-50 transition-colors"
      >
        <Heart 
          className={`w-5 h-5 ${isLiked ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} 
        />
      </button>

      {/* Product image with hover effect */}
      <div 
        className="relative h-64 overflow-hidden"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <img 
          src={product.image} 
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        
        {/* Hover overlay */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent flex items-end"
            >
              <button className="w-full py-3 bg-white/20 backdrop-blur-sm text-white font-semibold flex items-center justify-center space-x-2">
                <Eye className="w-4 h-4" />
                <span>Quick View</span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Product info */}
      <div className="p-6">
        {/* Category */}
        <div className="flex items-center justify-between mb-3">
          <span className="px-3 py-1 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 text-xs font-semibold rounded-full">
            {product.category}
          </span>
          <div className="flex items-center space-x-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
            ))}
          </div>
        </div>

        {/* Name and description */}
        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-amber-700 transition-colors">
          {product.name}
        </h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {product.description}
        </p>

        {/* Price and weight */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-baseline space-x-2">
            <span className="text-3xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
              ${product.price.toFixed(2)}
            </span>
            <span className="text-gray-500">/kg</span>
          </div>
          <div className="flex items-center space-x-1 text-gray-600">
            <Scale className="w-4 h-4" />
            <span>{product.isWholesale ? 'Min. ' : ''}{product.weight}</span>
          </div>
        </div>

        {/* Purchase options */}
        {product.isActive && (
          <div className="space-y-4">
            {/* Purchase type selector */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'weight', label: 'By Weight', icon: <Scale className="w-4 h-4" /> },
                { id: 'customWeight', label: 'Custom', icon: <Scale className="w-4 h-4" /> },
                { id: 'amount', label: 'By Amount', icon: <DollarSign className="w-4 h-4" /> },
              ].map((type) => (
                <button
                  key={type.id}
                  onClick={() => setPurchaseType(type.id)}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl transition-all ${
                    purchaseType === type.id
                      ? 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 shadow-inner'
                      : 'hover:bg-gray-50 border border-gray-100'
                  }`}
                >
                  <div className={`mb-1 ${purchaseType === type.id ? 'text-amber-600' : 'text-gray-400'}`}>
                    {type.icon}
                  </div>
                  <span className={`text-xs font-medium ${purchaseType === type.id ? 'text-amber-700' : 'text-gray-600'}`}>
                    {type.label}
                  </span>
                </button>
              ))}
            </div>

            {/* Input fields based on selection */}
            <AnimatePresence mode="wait">
              {purchaseType === 'weight' && (
                <motion.div
                  key="weight"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-xl">
                    <span className="text-gray-700 font-medium">Quantity:</span>
                    <div className="flex items-center space-x-4">
                      <button 
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-8 h-8 flex items-center justify-center bg-white border border-amber-200 rounded-full hover:bg-amber-50 transition-colors"
                      >
                        -
                      </button>
                      <span className="text-xl font-bold text-amber-700 min-w-[40px] text-center">
                        {quantity}
                      </span>
                      <button 
                        onClick={() => setQuantity(quantity + 1)}
                        className="w-8 h-8 flex items-center justify-center bg-white border border-amber-200 rounded-full hover:bg-amber-50 transition-colors"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="text-center">
                    <span className="text-gray-600">Total: </span>
                    <span className="text-2xl font-bold text-amber-700">
                      ${(product.price * quantity).toFixed(2)}
                    </span>
                  </div>
                </motion.div>
              )}

              {purchaseType === 'customWeight' && (
                <motion.div
                  key="customWeight"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Weight (kg)
                    </label>
                    <input
                      type="number"
                      value={customWeight}
                      onChange={(e) => setCustomWeight(e.target.value)}
                      placeholder="e.g., 1.5"
                      step="0.1"
                      min="0.1"
                      className="w-full px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent"
                    />
                  </div>
                  {calculatedAmount && (
                    <div className="text-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                      <span className="text-gray-600">Amount: </span>
                      <span className="text-xl font-bold text-emerald-700">
                        ${calculatedAmount}
                      </span>
                    </div>
                  )}
                </motion.div>
              )}

              {purchaseType === 'amount' && (
                <motion.div
                  key="amount"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount ($)
                    </label>
                    <input
                      type="number"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder="e.g., 30.00"
                      step="0.01"
                      min="0.01"
                      className="w-full px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent"
                    />
                  </div>
                  {calculatedWeight && (
                    <div className="text-center p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
                      <span className="text-gray-600">Weight: </span>
                      <span className="text-xl font-bold text-blue-700">
                        {calculatedWeight} kg
                      </span>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Add to cart button */}
            <motion.button
              id={`add-btn-${product.id}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleAddToCart}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center space-x-2 ${
                product.isWholesale
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg shadow-green-500/30'
                  : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg shadow-amber-500/30'
              }`}
            >
              <ShoppingCart className="w-5 h-5" />
              <span>
                {!product.isActive ? 'Out of Stock' : 
                 product.isWholesale ? 'Add Wholesale to Cart' : 'Add to Cart'}
              </span>
            </motion.button>
          </div>
        )}
      </div>

      {/* Glowing border effect */}
      <div className="absolute inset-0 border-2 border-transparent group-hover:border-amber-200/50 rounded-3xl transition-all duration-500 pointer-events-none"></div>
    </motion.div>
  );
}

export default ProductCard;